import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import SheetModal from '@/components/General/SheetModal';
import Input from '@/components/General/inputs/Input';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { startAutomationRun } from '@/data/automations';
import {
    declaredInputFields,
    runStartPersonRequirement,
    buildDeclaredInputValues,
    missingRequiredInputKeys,
    buildStartRunInput,
} from '@/utils/automationBuilder';

/**
 * StartRunSheet — the v2 declared-input start form (§16.1, P9). Shown for
 * MANUAL / PATH_ACTION definitions before automation_start_run:
 *
 *  - Collects each declared inputSchema field with a type-aware input.
 *  - The person picker follows the run contract: required when the registry/
 *    catalog says personOptional=false, optional otherwise — GENERIC defs
 *    may run personless by design and never get a hidden default person.
 *  - A replayed start (same dedupe outcome) resolves to the existing run —
 *    surfaced as "already running", never as an error.
 *
 * Props:
 *  - isOpen, onClose
 *  - path: the v2 Paths row (inputSchema, id, name)
 *  - registry: automation_get_variables result (personOptional) — optional
 *  - catalogRow: the trigger's AutomationTriggerCatalog row — optional
 *  - onStarted(result): called with the automation_start_run response;
 *    result.deduped distinguishes a replayed start.
 */
const StartRunSheet = ({ isOpen, onClose, path, registry, catalogRow, onStarted }) => {
    const fields = useMemo(() => declaredInputFields(path), [path]);
    const personRequirement = useMemo(
        () => runStartPersonRequirement({ registry, catalogRow }),
        [registry, catalogRow]);

    const [draft, setDraft] = useState({});
    const [person, setPerson] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setDraft({});
            setPerson(null);
        }
    }, [isOpen]);

    const missingKeys = useMemo(() => missingRequiredInputKeys({ fields, draft }), [fields, draft]);
    const personMissing = personRequirement === 'required' && !person?.id;
    const canSubmit = missingKeys.length === 0 && !personMissing && !saving;

    const setField = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));

    const renderFieldInput = (field) => {
        const title = `${field.label || field.key}${field.required ? ' *' : ''}`;
        const value = draft[field.key];
        if (field.semantic === 'personID') {
            return (
                <SheetSelector
                    key={field.key}
                    type="people"
                    title={title}
                    placeholder="Select a person"
                    value={value && typeof value === 'object' ? value : null}
                    onChange={(p) => setField(field.key, p?.id ? { id: p.id, ...p } : undefined)}
                    required={field.required === true}
                    testID={`start-run-input-${field.key}`}
                />
            );
        }
        switch (field.type) {
            case 'boolean':
                return (
                    <ToggleRow
                        key={field.key}
                        label={title}
                        value={value === true}
                        onValueChange={(val) => setField(field.key, val)}
                    />
                );
            case 'number':
                return (
                    <Input
                        key={field.key}
                        title={title}
                        placeholder="0"
                        keyboardType="numeric"
                        value={value != null ? String(value) : ''}
                        onUpdate={(val) => setField(field.key, val)}
                    />
                );
            case 'datetime':
                return (
                    <DatePickerInput
                        key={field.key}
                        title={title}
                        mode="datetime"
                        minuteInterval={5}
                        value={value ? new Date(value) : null}
                        onUpdate={(val) => setField(field.key, val ? new Date(val).toISOString() : null)}
                    />
                );
            case 'uuid':
                return (
                    <Input
                        key={field.key}
                        title={title}
                        placeholder="Paste an ID..."
                        value={value || ''}
                        onUpdate={(val) => setField(field.key, val)}
                    />
                );
            default:
                return (
                    <Input
                        key={field.key}
                        title={title}
                        placeholder="Enter a value..."
                        value={value || ''}
                        onUpdate={(val) => setField(field.key, val)}
                    />
                );
        }
    };

    const handleSubmit = async () => {
        if (!canSubmit || !path?.id) return;
        setSaving(true);
        try {
            const input = buildDeclaredInputValues({ fields, draft });
            const result = await startAutomationRun({
                pathID: path.id,
                input: buildStartRunInput({ personID: person?.id, input }),
            });
            if (result?.deduped) {
                Toast.show({ type: 'info', text2: 'That run was already started — opening the existing run.' });
            } else {
                Toast.show({ type: 'success', text2: 'Run started!' });
            }
            onStarted?.(result);
            onClose?.();
        } catch {
            // startAutomationRun toasts contract errors itself.
        } finally {
            setSaving(false);
        }
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onCancel={onClose}
            onClose={onClose}
            title={`Start Run — ${path?.name || 'Automation'}`}
            cancelText="Cancel"
            confirmText="Start Run"
            onConfirm={handleSubmit}
            loading={saving}
            scrollable
        >
            <ScrollView keyboardShouldPersistTaps="handled">
                <Text style={styles.helpText}>
                    {personRequirement === 'required'
                        ? 'This automation runs for a specific person.'
                        : 'This automation can run for a person or with no person.'}
                </Text>

                <SheetSelector
                    type="people"
                    title={personRequirement === 'required' ? 'Person *' : 'Person (optional)'}
                    placeholder={personRequirement === 'required' ? 'Select a person' : 'Run with no person'}
                    value={person}
                    onChange={(p) => setPerson(p?.id ? p : null)}
                    required={personRequirement === 'required'}
                    testID="start-run-person"
                />

                {fields.length > 0 && (
                    <View style={styles.inputsBlock}>
                        <Text style={styles.inputsLabel}>Inputs</Text>
                        {fields.map(renderFieldInput)}
                    </View>
                )}

                <Text style={styles.helpText}>
                    Starting an identical run twice returns the existing run instead of duplicating work.
                </Text>
            </ScrollView>
        </SheetModal>
    );
};

const styles = StyleSheet.create((theme) => ({
    helpText: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        lineHeight: 17,
        marginBottom: 12,
    },
    inputsBlock: { marginTop: 8, marginBottom: 8 },
    inputsLabel: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 8,
    },
}));

export default StartRunSheet;
